import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { Col, Image, Link, Row, Text, useMedia } from '@opendoor/bricks-next';
import { IconArrowRight } from '@tabler/icons-react';
import { EntryComponent } from 'cms/entries/entries';
import {
  ILpComponentHeaderWithId,
  ILpComponentMedia,
  ILpComponentStandaloneText,
} from 'declarations/contentful';

import Container from './shared/Container';
import { Subhead, SubheadSmall, TitleWithAccent } from './shared/Typography';

interface BodyParserConfig {
  gridColumnConfig: string;
  blockquoteGridColumn: string;
  pGridColumn?: string;
  textAlign?: 'left' | 'center';
}

const bodyParser = ({
  gridColumnConfig,
  blockquoteGridColumn,
  pGridColumn,
  textAlign,
}: BodyParserConfig): Options => ({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <Text
        tag="p"
        typography="$bodyLarge"
        textAlign={textAlign}
        $platform-web={{ gridColumn: pGridColumn ?? gridColumnConfig }}
      >
        {children}
      </Text>
    ),
    [BLOCKS.QUOTE]: (node, _) => {
      const text = node.content
        .flatMap((p) => ('content' in p ? p.content : []))
        .map((t) => ('value' in t ? t.value : ''))
        .join('');
      return (
        <Row
          tag="blockquote"
          ml="$8"
          position="relative"
          $largerThanSM={{ ml: '$0' }}
          $platform-web={{ gridColumn: blockquoteGridColumn }}
        >
          <Text
            tag="span"
            aria-hidden
            typography="$headerMedium"
            position="absolute"
            $platform-web={{ right: 'calc(100% + 16px)' }}
          >
            “
          </Text>
          <Text tag="p" typography="$headerMedium" textAlign={textAlign}>
            {text}
          </Text>
        </Row>
      );
    },
    [BLOCKS.HEADING_1]: (_, children) => (
      <Text
        tag="h1"
        typography="$subheaderLarge"
        textAlign={textAlign}
        $platform-web={{ gridColumn: gridColumnConfig }}
      >
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_2]: (_, children) => (
      <Text
        tag="h2"
        typography="$subheaderLarge"
        textAlign={textAlign}
        $platform-web={{ gridColumn: gridColumnConfig }}
      >
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <Text
        tag="h3"
        typography="$subheaderMedium"
        textAlign={textAlign}
        $platform-web={{ gridColumn: gridColumnConfig }}
      >
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_4]: (_, children) => (
      <Text
        tag="h4"
        typography="$subheaderSmall"
        textAlign={textAlign}
        $platform-web={{ gridColumn: gridColumnConfig }}
      >
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_5]: (_, children) => (
      <Text
        tag="h5"
        typography="$bodyLarge"
        textAlign={textAlign}
        $platform-web={{ gridColumn: gridColumnConfig }}
      >
        {children}
      </Text>
    ),
    [BLOCKS.HEADING_6]: (_, children) => (
      <Text
        tag="h6"
        typography="$bodyMedium"
        textAlign={textAlign}
        $platform-web={{ gridColumn: gridColumnConfig }}
      >
        {children}
      </Text>
    ),
    [BLOCKS.UL_LIST]: (_, children) => (
      <Col tag="ul" $platform-web={{ gridColumn: gridColumnConfig }}>
        {children}
      </Col>
    ),
    [BLOCKS.OL_LIST]: (_, children) => (
      <Col tag="ol" $platform-web={{ gridColumn: gridColumnConfig }}>
        {children}
      </Col>
    ),
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const target: ILpComponentMedia | ILpComponentHeaderWithId = node.data.target;
      if (target.sys.contentType.sys.id === 'lpComponentMedia') {
        const { fields } = target as ILpComponentMedia;
        return (
          <Col
            $platform-web={{
              gridColumn: fields.size === 'Large' ? '1 / -1' : gridColumnConfig,
              scrollMarginTop: '80px',
            }}
          >
            {fields.image && (
              <Image
                source={{ uri: fields.image.fields?.file?.url ?? '' }}
                alt={fields.image.fields?.description || fields.image.fields?.title || ''}
                borderRadius="$8x"
              />
            )}
          </Col>
        );
      }
      const { fields } = target as ILpComponentHeaderWithId;
      return (
        <Col
          id={fields.elementID}
          $platform-web={{ gridColumn: gridColumnConfig, scrollMarginTop: '80px' }}
        >
          {fields.title && documentToReactComponents(fields.title)}
        </Col>
      );
    },
  },
});

const introOptions: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <Text tag="p" typography="$bodyLarge">
        {children}
      </Text>
    ),
  },
};

const sidebarParse: Options = {
  renderNode: {
    [BLOCKS.HEADING_2]: (_, children) => (
      <Text
        tag="h2"
        color="$contentSecondary"
        typography="$headerMedium"
        fontWeight="$medium"
        mb="$6"
        pb="$6"
        borderBottomColor="$borderPrimary"
      >
        {children}
      </Text>
    ),
    [BLOCKS.UL_LIST]: (_, children) => (
      <Col tag="ul" role="list" mb="$6" p="$0" $platform-web={{ listStyle: 'none' }}>
        {children}
      </Col>
    ),
    [BLOCKS.LIST_ITEM]: (_, children) => (
      <Col tag="li" mb="$6">
        {children}
      </Col>
    ),
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <Text tag="p" typography="$bodyMedium" color="$contentSecondary">
        {children}
      </Text>
    ),
    [INLINES.HYPERLINK]: (node, children) => (
      <Link
        href={node.data.uri}
        analyticsName="cosmos-landing-page-standalone-text-sidebar"
        fontWeight="$medium"
        mb="$2"
        $platform-web={{ display: 'block' }}
      >
        {children}
      </Link>
    ),
  },
};

const grid12Style = {
  display: 'grid' as const,
  gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
  columnGap: '16px',
};

const grid8Style = {
  display: 'grid' as const,
  gridTemplateColumns: 'repeat(8, minmax(0, 1fr))',
  columnGap: '16px',
};

// dialogId is for instances where the Standalone text is in a Sheet
const Header = ({
  entry,
  dialogId,
  centered,
}: {
  entry: ILpComponentStandaloneText;
  dialogId?: string;
  centered?: boolean;
}) => {
  const { fields } = entry;
  const { largerThanSM } = useMedia();
  const textAlign = centered && largerThanSM ? ('center' as const) : undefined;

  return (
    <Col tag="header" mb="$4" $largerThanSM={{ mb: '$6' }}>
      {fields.title && (
        <TitleWithAccent
          overRideTitleTag="h1"
          title={fields.title}
          titleAccent={fields.titleAccent}
          titleSize={fields.titleSize}
          marginHorizontal={fields.layout === 'Centered' ? 'auto' : undefined}
          textAlign={textAlign}
          id={dialogId || undefined}
        />
      )}
      {fields.subhead &&
        (fields.titleSize === 'Large' ? (
          <Subhead textAlign={textAlign}>{fields.subhead}</Subhead>
        ) : (
          <SubheadSmall textAlign={textAlign}>{fields.subhead}</SubheadSmall>
        ))}
    </Col>
  );
};

const Cta = ({ entry }: { entry: ILpComponentStandaloneText }) => {
  const { fields } = entry;

  return (
    <Link
      aria-label={fields.ctaDisplayText || undefined}
      analyticsName={`cosmos-landing-page-standalone-text-${fields?.analyticsId}`}
      href={fields.ctaUrl ?? ''}
    >
      <Row
        alignItems="center"
        gap="$2"
        mt="$6"
        $largerThanSM={{ mt: '$8' }}
        $platform-web={{ display: 'inline-flex' }}
      >
        <IconArrowRight size={15} />
        <Text
          marginTop={-1}
          tag="p"
          typography="$bodyMedium"
          fontWeight="$medium"
          color="$backgroundAccentBrand"
          whiteSpace="nowrap"
        >
          {fields.ctaDisplayText}
        </Text>
      </Row>
    </Link>
  );
};

const introColumnsStyle = {
  display: 'block' as const,
  columns: 'auto 3',
  columnGap: '24px',
};

const SidebarLayout = ({ entry }: { entry: ILpComponentStandaloneText }) => {
  const { fields } = entry;
  const { largerThanSM, largerThanMD } = useMedia();
  const resolvedGap = fields.bodyPGap ?? 64;
  const halfGap = resolvedGap / 2;

  return (
    <>
      <Col $platform-web={largerThanSM ? grid12Style : undefined}>
        <Col $platform-web={{ gridColumn: '1 / 9' }}>
          {(fields.title || fields.subhead) && <Header entry={entry} />}
          {fields.intro && (
            <Col
              gap="$8"
              mb="$8"
              $largerThanSM={{ mb: '$16' }}
              $platform-web={largerThanMD ? introColumnsStyle : undefined}
            >
              {documentToReactComponents(fields.intro, introOptions)}
            </Col>
          )}
          <Col
            mb="$4"
            $largerThanSM={{ mb: '$0' }}
            gap={halfGap}
            $platform-web={largerThanSM ? grid8Style : undefined}
          >
            {fields.body &&
              documentToReactComponents(
                fields.body,
                bodyParser({
                  gridColumnConfig: '1 / 7',
                  blockquoteGridColumn: '2 / -1',
                }),
              )}
          </Col>
        </Col>
        {fields.sidebar && (
          <Col $platform-web={{ gridColumn: '10 / -1' }}>
            {documentToReactComponents(fields.sidebar, sidebarParse)}
          </Col>
        )}
      </Col>
      {fields.ctaDisplayText && fields.ctaUrl && <Cta entry={entry} />}
    </>
  );
};

// Centered Layout is also used by Sheets
// dialogId is for instances where the Standalone text is in a Sheet
export const CenteredLayout = ({
  entry,
  dialogId,
}: {
  entry: ILpComponentStandaloneText;
  dialogId?: string;
}) => {
  const { fields } = entry;
  const { largerThanSM, largerThanMD } = useMedia();
  const resolvedGap = fields.bodyPGap ?? 64;
  const halfGap = resolvedGap / 2;

  return (
    <Col $platform-web={largerThanSM ? grid12Style : undefined}>
      <Col gap={halfGap} $platform-web={{ gridColumn: '3 / -3' }}>
        {(fields.title || fields.subhead) && <Header entry={entry} dialogId={dialogId} centered />}
      </Col>
      <Col
        gap={halfGap}
        $platform-web={
          largerThanSM ? { gridColumn: '3 / -3', ...grid8Style } : { gridColumn: '3 / -3' }
        }
      >
        {fields.intro && (
          <Col
            gap="$8"
            mb="$8"
            $largerThanSM={{ mb: '$16' }}
            $platform-web={(() => {
              if (largerThanMD) return { gridColumn: '1 / -1', ...introColumnsStyle };
              if (largerThanSM) return { gridColumn: '1 / -1' };
              return undefined;
            })()}
          >
            {documentToReactComponents(fields.intro, introOptions)}
          </Col>
        )}
        {fields.body &&
          documentToReactComponents(
            fields.body,
            bodyParser({
              gridColumnConfig: '2 / -2',
              blockquoteGridColumn: '2 / -2',
              textAlign: largerThanSM ? 'center' : undefined,
            }),
          )}
      </Col>
      <Col
        $platform-web={
          largerThanSM ? { gridColumn: '4 / -4', textAlign: 'center' } : { gridColumn: '4 / -4' }
        }
      >
        {fields.ctaDisplayText && fields.ctaUrl && <Cta entry={entry} />}
      </Col>
    </Col>
  );
};

const DefaultLayout = ({ entry }: { entry: ILpComponentStandaloneText }) => {
  const { fields } = entry;
  const { largerThanSM, largerThanMD } = useMedia();
  const resolvedGap = fields.bodyPGap ?? 64;
  const halfGap = resolvedGap / 2;

  return (
    <>
      {(fields.title || fields.subhead) && <Header entry={entry} />}

      {fields.intro && (
        <Col
          gap="$8"
          mb="$8"
          $largerThanSM={{ mb: '$16' }}
          $platform-web={largerThanMD ? introColumnsStyle : undefined}
        >
          {documentToReactComponents(fields.intro, introOptions)}
        </Col>
      )}
      <Col gap={halfGap} $platform-web={largerThanSM ? grid12Style : undefined}>
        {fields.body &&
          documentToReactComponents(
            fields.body,
            bodyParser({
              gridColumnConfig: '1 / 7',
              blockquoteGridColumn: '2 / 11',
              pGridColumn: fields.analyticsId === 'cash-plus-h2-standalone' ? '1 / 9' : undefined,
            }),
          )}
      </Col>
      {fields.ctaDisplayText && fields.ctaUrl && <Cta entry={entry} />}
    </>
  );
};

const RenderStandaloneText = (entry: ILpComponentStandaloneText) => {
  const { fields } = entry;

  return (
    <Container>
      {/* eslint-disable-next-line no-nested-ternary -- Grandfathered violation */}
      {fields.layout === 'Sidebar' ? (
        <SidebarLayout entry={entry} />
      ) : fields.layout === 'Centered' ? (
        <CenteredLayout entry={entry} />
      ) : (
        <DefaultLayout entry={entry} />
      )}
    </Container>
  );
};

const StandaloneText: EntryComponent<ILpComponentStandaloneText> = {
  render: RenderStandaloneText,
};

export default StandaloneText;
